import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import {
  ColorThemes,
  LOCALSTORAGE_PREFIX,
  type ReducedMotionTypes,
} from "../app/constants";
import {
  FontSizeEnum,
  type FontSizeItem,
  type SupportedLanguage,
} from "../config/types";
import { RouteType } from "../directions/types";
import { safeLocalStorage } from "../utils/utils";
import {
  getInitialLanguage,
  getInitialValue,
  getStoredValue,
} from "./initialValues";

export enum UserPreferenceKeys {
  LANGUAGE = "i18n.language",
  DEFAULT_ROUTE_TYPE = "directions.defaultRouteType",
  EXCLUDED_TAGS = "directions.excludedTags",
  COLOR_THEME = "styles.theme.colorTheme",
  // Keep the last non-accessible theme separate so high contrast can be
  // disabled after a reload even if the active cached theme is accessible.
  LAST_NON_ACCESSIBLE_COLOR_THEME = "styles.theme.lastNonAccessibleColorTheme",
  REDUCED_MOTION = "ui.reducedMotion",
  FONT_SIZE = "ui.fontSize",
  SPEAK_ALOUD_INDICATIONS = "navigation.speak_aloud_indications",
}

const USER_COLOR_THEMES = new Set(Object.values(ColorThemes));

const isUserColorTheme = (value: unknown): value is ColorThemes => {
  return USER_COLOR_THEMES.has(value as ColorThemes);
};

const getInitialUserPreferencesColorTheme = (): ColorThemes => {
  const userPreferenceColorTheme = getStoredValue<ColorThemes>(
    UserPreferenceKeys.LAST_NON_ACCESSIBLE_COLOR_THEME,
  );

  if (isUserColorTheme(userPreferenceColorTheme)) {
    return userPreferenceColorTheme;
  }

  const colorTheme = getStoredValue<ColorThemes>(UserPreferenceKeys.COLOR_THEME);

  return isUserColorTheme(colorTheme) ? colorTheme : ColorThemes.system;
};

export interface UserPreferencesState {
  language: SupportedLanguage;
  defaultRouteType: RouteType;
  excludedTags: string[];
  colorTheme: ColorThemes;
  reducedMotion: ReducedMotionTypes;
  fontSize: FontSizeItem;
  speakAloudIndications: boolean;
}

const initialState: UserPreferencesState = {
  colorTheme: getInitialUserPreferencesColorTheme(),
  defaultRouteType: getInitialValue<RouteType>(
    UserPreferenceKeys.DEFAULT_ROUTE_TYPE,
    RouteType.CHOOSE_SHORTEST,
  ),
  excludedTags: getInitialValue<string[]>(UserPreferenceKeys.EXCLUDED_TAGS, []),
  fontSize: getInitialValue<FontSizeItem>(
    UserPreferenceKeys.FONT_SIZE,
    FontSizeEnum.MD as FontSizeItem,
  ),
  language: getInitialLanguage(),
  reducedMotion: getInitialValue<ReducedMotionTypes>(
    UserPreferenceKeys.REDUCED_MOTION,
    "user",
  ),
  speakAloudIndications: getInitialValue(
    UserPreferenceKeys.SPEAK_ALOUD_INDICATIONS,
    false,
  ),
};

interface SetUserConfigItemPayload<K extends keyof UserPreferencesState> {
  key: K;
  value: UserPreferencesState[K];
  storageKey: string;
}

const updateUserConfigItem = <K extends keyof UserPreferencesState>(
  state: UserPreferencesState,
  payload: SetUserConfigItemPayload<K>,
) => {
  const { key, value, storageKey } = payload;

  state[key] = value;

  safeLocalStorage.setItem(
    LOCALSTORAGE_PREFIX + storageKey,
    JSON.stringify(value),
  );
};

const UserPreferencesSlice = createSlice({
  initialState,
  name: "userPreferences",
  reducers: {
    hydrateUserPreferencesColorTheme: (
      state: UserPreferencesState,
      action: PayloadAction<ColorThemes>,
    ) => {
      state.colorTheme = action.payload;
    },
    setUserLanguage: (
      state: UserPreferencesState,
      action: PayloadAction<SupportedLanguage>,
    ) => {
      updateUserConfigItem(state, {
        key: "language",
        storageKey: "i18n.language",
        value: action.payload,
      });
    },
    setUserPreferencesColorTheme: (
      state: UserPreferencesState,
      action: PayloadAction<ColorThemes>,
    ) => {
      state.colorTheme = action.payload;
      safeLocalStorage.setItem(
        `${LOCALSTORAGE_PREFIX}${UserPreferenceKeys.LAST_NON_ACCESSIBLE_COLOR_THEME}`,
        action.payload,
      );
    },
    // others future setters
  },
});

export const {
  hydrateUserPreferencesColorTheme,
  setUserLanguage,
  setUserPreferencesColorTheme,
} = UserPreferencesSlice.actions;

// Selectors
const sliceData = (state: RootState) => state.userPreferences;

export const selectUserPreferencesTheme = createSelector(sliceData, (item) => {
  return item.colorTheme;
});

export default UserPreferencesSlice.reducer;
